import { HideWrapper } from "@portal/BaseComponents/HideWrapper/HideWrapper";
import { PORTAL_TABS } from "@portal/Components/Portal/PotalTabs/types";
import { useFormats } from "@portal/hooks/use-formats";
import useOpen from "@portal/hooks/useOpen";
import { customFieldAtom } from "@portal/store/Filters/customFields/custom-field";
import { resetPage, tableAtom } from "@portal/store/table/table";
import {
  CustomField,
  CustomFieldBetweenValues,
  CustomFieldFilterValues,
  CustomFieldInValues,
  CustomFieldOperatorType,
  CustomFiledMultiSelectValues,
} from "@portal/types/entities/custom-field";
import { filterUnassigned } from "@portal/util/predicates";
import produce from "immer";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { FilterDropdownDown } from "../../FilterDropdown/FilterDropdownDown";
import { getDateRangeText } from "../DateRange/utils";
import {
  CustomFieldDateFilter,
  DateSelectedValue,
} from "./CustomFieldDateFilter";
import { CustomFieldMultiSelect } from "./CustomFiledMultiSelect/CustomFieldMultiSelect";
import {
  CustomFieldRangeValueType,
  NumericCustomField,
} from "./Numeric/NumericCustomField";
import { getNumberBasedText } from "./predicate";

interface CustomFiledFilterGenericProps {
  customField: CustomField;
  customFieldKey: PORTAL_TABS[number];
}

function CustomFiledFilterGeneric(props: CustomFiledFilterGenericProps) {
  const { customField, customFieldKey } = props;
  const [customFiledFilter, setCustomFieldFilter] = useAtom(customFieldAtom);
  const setTable = useSetAtom(tableAtom);
  const filterValues = customFiledFilter.filterValues[customFieldKey];
  const { open, onClose, toggleOpen, handleOpenChange } = useOpen({
    open: false,
  });
  const { dateformat } = useFormats();
  const isNonNumeric = [
    "BOOLEAN",
    "DATE",
    "DATETIME",
    "TEXT",
    "SINGLE_SELECT",
    "PARAGRAPH",
  ].includes(customField.field_type);
  const isDateType = ["DATE", "DATETIME"].includes(customField.field_type);

  const getFilterDateSelectedValues = useCallback(():
    | DateSelectedValue
    | undefined => {
    const selectedValues = filterValues?.find(
      (field) => field.id === customField.id
    );

    if (isDateType && selectedValues) {
      return {
        include_unassigned: selectedValues.include_unassigned,
        value: selectedValues.operator.operands
          .value as CustomFieldBetweenValues,
        type: selectedValues.operator.type,
      date_range: selectedValues.date_range,
      };
    }
    return undefined;
  }, [filterValues, customField.id, isDateType]);

  function handleHideReset() {
    return !multiSelectSelectedValues?.length;
  }

  function handleNumericHideRest() {
    return !numericValues?.value;
  }
  function handleNonNumericSubmit(value: CustomFiledMultiSelectValues) {
    setCustomFieldFilter((customFields) => {
      return produce(customFields, (draft) => {
        const index = draft.filterValues[customFieldKey].findIndex(
          (field) => field.id === customField.id
        );

        if (index !== -1) {
          draft.filterValues[customFieldKey][index] = {
            id: customField.id,
            ...value,
          };
          return;
        }

        draft.filterValues[customFieldKey].push({
          id: customField.id,
          ...value,
        });
      });
    });
    onClose();
    setTable(resetPage);
  }
  function handleNumericSubmit(
    value: Omit<CustomFieldFilterValues<CustomFieldOperatorType>, "id">
  ) {
    setCustomFieldFilter((customFields) => {
      return produce(customFields, (draft) => {
        const index = draft.filterValues[customFieldKey].findIndex(
          (field) => field.id === customField.id
        );

        if (index !== -1) {
          draft.filterValues[customFieldKey][index] = {
            id: customField.id,
            ...value,
          };
          return;
        }

        draft.filterValues[customFieldKey].push({
          id: customField.id,
          ...value,
        });
      });
    });
    onClose();
    setTable(resetPage);
  }

  function handleDateSubmit(
    value: Omit<CustomFieldFilterValues<CustomFieldOperatorType>, "id">
  ) {
    setCustomFieldFilter((customFields) => {
      return produce(customFields, (draft) => {
        const index = draft.filterValues[customFieldKey].findIndex(
          (field) => field.id === customField.id
        );

        if (index !== -1) {
          draft.filterValues[customFieldKey][index] = {
            id: customField.id,
            ...value,
          };
          return;
        }

        draft.filterValues[customFieldKey].push({
          id: customField.id,
          ...value,
        });
      });
    });
    onClose();
    setTable(resetPage);
  }

  function handleRemove() {
    setCustomFieldFilter((customFields) => {
      return produce(customFields, (draft) => {
        draft.selectedKeys[customFieldKey] = draft.selectedKeys[
          customFieldKey
        ]?.filter((field) => field !== customField.id);
        draft.filterValues[customFieldKey] = draft.filterValues[
          customFieldKey
        ]?.filter((field) => field.id !== customField.id);
      });
    });

    onClose();
    setTable(resetPage);
  }

  function handleReset() {
    setCustomFieldFilter((customFields) => {
      return produce(customFields, (draft) => {
        draft.filterValues[customFieldKey] = draft.filterValues[
          customFieldKey
        ]?.filter((field) => field.id !== customField.id);
      });
    });
    onClose();
    setTable(resetPage);
  }

  function getFilterMultiselectValues() {
    const selectedValues = filterValues?.find(
      (field) => field.id === customField.id
    );

    if (isNonNumeric && selectedValues?.operator.type === "IN") {
      return selectedValues.include_unassigned
        ? [
            ...(selectedValues?.operator.operands.value as CustomFieldInValues),
            "-1",
          ]
        : (selectedValues?.operator.operands.value as CustomFieldInValues);
    }
    return [];
  }
  function getFilterNumericSelectedValues():
    | CustomFieldRangeValueType<CustomFieldOperatorType>
    | undefined {
    const selectedValues = filterValues?.find(
      (field) => field.id === customField.id
    );

    if (
      !isNonNumeric &&
      selectedValues &&
      Object.values(selectedValues).length
    ) {
      return {
        include_unassigned: selectedValues.include_unassigned,
        value: selectedValues.operator.operands.value,
        type: selectedValues.operator.type,
      };
    }
    return;
  }

  const numericValues = useMemo(getFilterNumericSelectedValues, [
    customField.id,
    filterValues,
    isNonNumeric,
  ]);

  const multiSelectSelectedValues = useMemo(getFilterMultiselectValues, [
    customField.id,
    filterValues,
    isNonNumeric,
  ]);

  function getNumericText() {
    return numericValues?.type === "IN" && numericValues.include_unassigned
      ? "Is Unassigned"
      : getNumberBasedText(numericValues);
  }

  function getNonNumericText() {
    return multiSelectSelectedValues.includes("-1")
      ? ["Unassigned", ...multiSelectSelectedValues]
          .filter(filterUnassigned)
          .join(", ")
      : multiSelectSelectedValues.toString() === ""
      ? "Choose Value"
      : multiSelectSelectedValues.length === customField.options.length
      ? "All"
      : multiSelectSelectedValues.join(", ");
  }

  const numericText = useMemo(getNumericText, [numericValues]);
  const nonNumericText = useMemo(getNonNumericText, [
    customField.options.length,
    multiSelectSelectedValues,
  ]);
  const hideReset = useMemo(handleHideReset, [
    multiSelectSelectedValues?.length,
  ]);
  const hideNumericReset = useMemo(handleNumericHideRest, [
    numericValues?.value,
  ]);

  function getDateFilterText() {
    const dateValues = getFilterDateSelectedValues();
    if (!dateValues) return "Choose Date";

    return getDateRangeText(
      {
        from: (dateValues.value as { from?: string; to?: string })?.from,
        to: (dateValues.value as { from?: string; to?: string })?.to,
        type: dateValues.date_range,
      },
      dateformat
    );
  }

  const hideDateReset = useMemo(() => {
    const dateValues = getFilterDateSelectedValues();
    if (!dateValues) return !dateValues;

    const value = dateValues.value;
    return !value.from && !value.to;
  }, [getFilterDateSelectedValues]);

  const nonNumeric = (
    <CustomFieldMultiSelect
      options={customField.options}
      onSubmit={handleNonNumericSubmit}
      initialValue={multiSelectSelectedValues}
      onCancel={onClose}
      shouldIncludeUnassigned
    />
  );

  const Numeric = (
    <NumericCustomField
      value={numericValues}
      allowDecimal={customField.field_type === "DECIMAL"}
      onCancel={onClose}
      onSubmit={handleNumericSubmit}
    />
  );

  const DateFilter = (
    <CustomFieldDateFilter
      value={getFilterDateSelectedValues()}
      onCancel={onClose}
      onSubmit={handleDateSubmit}
    />
  );

  const isActive =
    customFiledFilter.selectedKeys[customFieldKey]?.includes(customField.id) ??
    true;

  function getFilterComponent() {
    if (isDateType) {
      return DateFilter;
    }
    return isNonNumeric ? nonNumeric : Numeric;
  }

  function getFilterText() {
    if (isDateType) {
      return getDateFilterText();
    }
    return isNonNumeric ? nonNumericText : numericText;
  }

  function getHideReset() {
    if (isDateType) {
      return hideDateReset;
    }
    return isNonNumeric ? hideReset : hideNumericReset;
  }

  const hideResetValue = getHideReset();
  const filterTextValue = getFilterText();
  const filterComponentValue = getFilterComponent();

  return (
    <HideWrapper hide={!isActive}>
      <FilterDropdownDown
        filterBlockProps={{
          title: customField.display_name,
          open,
          toggleOpen,
          onRemove: handleRemove,
          onReset: handleReset,
          hideReset: hideResetValue,
          filterValues: filterTextValue,
          hasSymbol: true,
        }}
        filterComponent={filterComponentValue}
        filterTitle={customField.display_name}
        onopenChange={handleOpenChange}
      />
    </HideWrapper>
  );
}

export { CustomFiledFilterGeneric };
